import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
  useRef,
  type ClipboardEvent as ReactClipboardEvent,
} from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import { useQuery } from '@tanstack/react-query';
import type { Attachment } from '@rundown/types';
import { cx } from '../../lib/cx';
import { editorExtensions } from './extensions';
import { Toolbar } from './Toolbar';
import { isFormattedJson, prettyJson } from './highlighting';
import { filesFromClipboard, filesFromDrop, insertImages, splitByDestination } from './attachments';
import { stripTransientAttrs } from './imageNode';
import { MentionMenu } from './MentionMenu';
import { mentionablePeopleQueryOptions, type MentionCandidate } from './mentionsApi';
import { RUNDOWN_BOT_ID, RUNDOWN_BOT_NAME } from '@rundown/types';

const RUNDOWN_CANDIDATE = { id: RUNDOWN_BOT_ID, name: RUNDOWN_BOT_NAME, email: '' } as const;

/**
 * The reply composer — a Tiptap editor in place of the plain `<textarea>` it
 * replaces in `RequestDetailPage.tsx`, so `message.body` can become the real
 * ProseMirror document `Message.body` is typed for instead of a plain string.
 *
 * WHY NO `@tiptap/suggestion`: that is the package Tiptap's own
 * `@tiptap/extension-mention` is built on, and neither it nor the mention
 * extension itself is installed — this task adds no new dependencies. The `@`
 * popup below is built directly on primitives `@tiptap/react` already
 * re-exports from `@tiptap/core` (`useEditor`, `editorProps.handleKeyDown`,
 * `editor.view.coordsAtPos`) rather than on that utility. It is a smaller
 * feature set (no fuzzy ranking, no portal-based positioning) but a complete
 * one for a workspace with a handful of members.
 *
 * WHY NO TOOLBAR: bold/italic/code/link and the two list types all come with
 * StarterKit's usual keyboard shortcuts and markdown-style input rules
 * (`**bold**`, `` `code` ``, `- ` for a bullet, `` ``` `` for a code block) for
 * free. A support-thread reply box is a small, frequent thing to open; a
 * button row would cost more space than the feature is worth here.
 */

export interface ComposerHandle {
  focus(): void;
  /** Wipe the document back to empty. Called after a successful post. */
  clear(): void;
  /** The current document, in the exact JSON shape the API's allowlist validates. */
  getContent(): unknown;
  /** Nothing typed and nothing pasted, asked on demand — `onEmptyChange`
   * reports the same thing as it changes, for callers that gate a button. */
  isEmpty(): boolean;
}

export interface ComposerProps {
  readonly workspaceId: string;
  readonly placeholder: string;
  /**
   * Cmd/Ctrl+Enter was pressed. The caller reads content via the ref and posts
   * it. Absent on a field that is saved with a button rather than sent — the
   * description — where a shortcut that silently commits prose written in
   * several passes is the wrong behaviour.
   */
  readonly onSubmit?: () => void;
  /** Fires whenever emptiness changes, so the caller can gate its Send button. */
  readonly onEmptyChange?: (empty: boolean) => void;
  /** What the editor starts with. A stored document, or plain text Tiptap
   * parses into paragraphs. Read once, when the editor is created. */
  readonly initialContent?: unknown;
  /** Announced to assistive tech, and what a test looks the field up by. */
  readonly ariaLabel?: string;
  /** Show the formatting controls. Off in the thread, on for the description —
   * see Toolbar. */
  readonly toolbar?: boolean;
  /**
   * `useUploader().handlePaste` — returns true when it took a pasted file, in
   * which case ProseMirror must not also try to paste it as content.
   */
  readonly onPasteFiles: (event: ReactClipboardEvent) => boolean;
  /**
   * Uploads files and returns the attachments that landed, so a pasted
   * screenshot can go INLINE rather than into the strip. Absent falls back to
   * `onPasteFiles`, which attaches everything — the behaviour before images
   * could be inline.
   */
  readonly onUpload?: (files: File[], destination: 'inline' | 'attach') => Promise<readonly Attachment[]>;
  /**
   * How much room the box takes.
   *
   * `reply` is the thread's box: one line until it grows, at the 13px the
   * conversation is set in. `ask` is Home's front door — the first thing on an
   * otherwise empty screen, written into before anything else exists — so it
   * is larger, and holds three lines of height whether or not anything has
   * been typed yet.
   */
  readonly size?: 'reply' | 'ask';
}

interface MentionQuery {
  readonly from: number;
  readonly to: number;
  readonly text: string;
}

const MAX_MENTION_RESULTS = 8;
/** Object Replacement Character — see `findMentionQuery` for why. */
const LEAF_PLACEHOLDER = '￼';

/**
 * Is the cursor sitting right after an unfinished `@query`? Reimplements just
 * enough of what `@tiptap/suggestion` would do: look at the text immediately
 * before the cursor in the current paragraph, and match a trailing
 * `@word` that starts at the beginning of the paragraph or after whitespace —
 * the same rule that keeps "email@domain.com" from triggering the picker.
 *
 * `textBetween`'s `leafText` argument is passed a one-character placeholder
 * rather than left as the default empty string: a hardBreak or an existing
 * mention chip is one ProseMirror position wide, and the position arithmetic
 * below (`$from.pos - query.length - 1`) only lands on the right character if
 * the string's length tracks position deltas exactly.
 */
function findMentionQuery(editor: Editor): MentionQuery | null {
  const { selection } = editor.state;
  if (!selection.empty) return null;

  const { $from } = selection;
  if (!$from.parent.isTextblock || $from.parent.type.name === 'codeBlock') return null;

  const textBefore = $from.parent.textBetween(0, $from.parentOffset, undefined, LEAF_PLACEHOLDER);
  const match = /(?:^|[\s ])@([^\s@]{0,64})$/.exec(textBefore);
  if (match === null) return null;

  const query = match[1] ?? '';
  const from = $from.pos - query.length - 1;
  return { from, to: $from.pos, text: query };
}

export const Composer = forwardRef<ComposerHandle, ComposerProps>(function Composer(
  {
    workspaceId,
    placeholder,
    onSubmit,
    onEmptyChange,
    onPasteFiles,
    onUpload,
    initialContent,
    ariaLabel = 'Write an update',
    toolbar = false,
    size = 'reply',
  },
  ref,
) {
  const peopleQuery = useQuery(mentionablePeopleQueryOptions(workspaceId));
  // @rundown heads the list: it can be addressed from any composer, and it is
  // not a person the server's roster would ever return.
  const people = [RUNDOWN_CANDIDATE, ...(peopleQuery.data?.items ?? [])];

  const [isEmpty, setIsEmpty] = useState(true);
  const [mentionQuery, setMentionQuery] = useState<MentionQuery | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ left: number; top: number } | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const candidates: readonly MentionCandidate[] =
    mentionQuery === null
      ? []
      : people
          .filter((person) => person.name.toLowerCase().includes(mentionQuery.text.toLowerCase()))
          .slice(0, MAX_MENTION_RESULTS);

  // Mirrors of the latest render's values, read from inside the stable
  // `editorProps` callbacks below — those are captured once when the editor
  // is constructed (see the `useEditor` deps array) and must not go stale.
  // Assigning during render, rather than in a `useEffect`, keeps them correct
  // even for the very first keystroke that changes them.
  const mentionQueryRef = useRef<MentionQuery | null>(null);
  mentionQueryRef.current = mentionQuery;
  const candidatesRef = useRef<readonly MentionCandidate[]>([]);
  candidatesRef.current = candidates;
  const highlightedIndexRef = useRef(0);
  highlightedIndexRef.current = highlightedIndex >= candidates.length ? 0 : highlightedIndex;
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  const onPasteFilesRef = useRef(onPasteFiles);
  onPasteFilesRef.current = onPasteFiles;
  const onUploadRef = useRef(onUpload);
  onUploadRef.current = onUpload;
  const editorRef = useRef<Editor | null>(null);
  // Set below, once `editor` exists — `handleKeyDown` needs it but is defined
  // as part of the same `useEditor` call that produces `editor`, so the ref is
  // the only way to break the circularity.
  const selectCandidateRef = useRef<(candidate: MentionCandidate) => void>(() => {});

  const rescanMention = useCallback((instance: Editor) => {
    setIsEmpty(instance.isEmpty);
    const next = findMentionQuery(instance);
    setMentionQuery(next);
    if (next === null) return;
    const coords = instance.view.coordsAtPos(next.from);
    setMenuPosition({ left: coords.left, top: coords.bottom + 4 });
  }, []);

  const editor = useEditor(
    {
      extensions: editorExtensions(),
      // Read once, when the editor is created — which is what the caller
      // means by "initial". A field that needs to be re-seeded from a
      // different source remounts with a new `key` instead, so a realtime
      // update can never overwrite what someone is midway through typing.
      content: (initialContent ?? '') as never,
      editorProps: {
        attributes: {
          // A bare `contenteditable` div is exposed as a generic container,
          // not as a text field — so a screen reader announces the label and
          // then nothing about what the thing IS.
          role: 'textbox',
          'aria-multiline': 'true',
          'aria-label': ariaLabel,
          class: 'fs-13 leading-relaxed focus:outline-none min-h-[2.6em]',
        },
        // See RichTextEditor.handleDrop: without this the browser navigates
        // to a dropped file and the reply being written is lost.
        handleDrop: (_view, event, slice) => {
          if (slice.content.size > 0) return false;
          const files = filesFromDrop(event as unknown as DragEvent);
          if (files.length === 0) return false;
          const upload = onUploadRef.current;
          if (upload === undefined) return false;

          event.preventDefault();
          const { inline, attach } = splitByDestination(files);
          if (attach.length > 0) void upload(attach, 'attach');
          if (inline.length > 0) {
            void upload(inline, 'inline').then((attachments) => {
              const current = editorRef.current;
              if (current !== null) insertImages(current, attachments);
            });
          }
          return true;
        },
        handlePaste: (_view, event) => {
          // Images go inline; anything else falls through to the strip. Both
          // need the upload to have landed before the node can name it — see
          // attachments.tsx.
          const upload = onUploadRef.current;
          if (upload !== undefined) {
            const files = filesFromClipboard(event as unknown as ClipboardEvent);
            if (files.length > 0) {
              event.preventDefault();
              const { inline, attach } = splitByDestination(files);
              if (attach.length > 0) void upload(attach, 'attach');
              if (inline.length > 0) {
                void upload(inline, 'inline').then((attachments) => {
                  const current = editorRef.current;
                  if (current !== null) insertImages(current, attachments);
                });
              }
              return true;
            }
          }
          /**
           * A pasted JSON payload lands in a code block, formatted.
           *
           * Pasting `{"a":1,"b":[2,3]}` into a paragraph gives an unreadable
           * run of text with its quotes possibly smartened by the browser; a
           * code block preserves it exactly and the highlighter can colour it.
           * Only for a whole object or array — `prettyJson` refuses a bare
           * number, so pasting `42` is not treated as a document.
           *
           * Reversible: it is one transaction, so ⌘Z puts the plain paste back.
           */
          const text = event.clipboardData?.getData('text/plain');
          if (text !== undefined && text !== '') {
            const pretty = prettyJson(text);
            if (pretty !== null || isFormattedJson(text)) {
              const { schema, tr } = _view.state;
              const codeBlock = schema.nodes['codeBlock'];
              if (codeBlock !== undefined) {
                event.preventDefault();
                _view.dispatch(
                  tr
                    .replaceSelectionWith(
                      codeBlock.create({ language: 'json' }, schema.text(pretty ?? text.trim())),
                    )
                    .scrollIntoView(),
                );
                return true;
              }
            }
          }

          return (
            // Native `ClipboardEvent` here, not React's synthetic one — only
            // `.clipboardData` and `.preventDefault()` are used by the handler,
            // both present on the native event, so the cast is safe. Going
            // through ProseMirror's own `handlePaste` (rather than a React
            // `onPaste` prop on a wrapper element) matters: it runs *before*
            // ProseMirror's default paste handling, so calling
            // `preventDefault()` here actually stops a dropped screenshot from
            // also being inserted as pasted content.
            onPasteFilesRef.current(event as unknown as ReactClipboardEvent) === true
          );
        },
        handleKeyDown: (_view, event) => {
          const query = mentionQueryRef.current;
          const list = candidatesRef.current;
          if (query !== null && list.length > 0) {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setHighlightedIndex((index) => (index + 1) % list.length);
              return true;
            }
            if (event.key === 'ArrowUp') {
              event.preventDefault();
              setHighlightedIndex((index) => (index - 1 + list.length) % list.length);
              return true;
            }
            if (event.key === 'Enter' || event.key === 'Tab') {
              event.preventDefault();
              const candidate = list[highlightedIndexRef.current];
              if (candidate !== undefined) selectCandidateRef.current(candidate);
              return true;
            }
            if (event.key === 'Escape') {
              setMentionQuery(null);
              return true;
            }
          }
          if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
            const submit = onSubmitRef.current;
            if (submit === undefined) return false;
            event.preventDefault();
            submit();
            return true;
          }
          return false;
        },
      },
      // An editor opened on existing words (editing a message) is not empty,
      // and must not draw the placeholder over them or gate its Save on
      // "nothing typed" — `isEmpty` starts true and otherwise only moves on
      // the first keystroke.
      onCreate: ({ editor: instance }) => setIsEmpty(instance.isEmpty),
      onUpdate: ({ editor: instance }) => rescanMention(instance),
      onSelectionUpdate: ({ editor: instance }) => rescanMention(instance),
    },
    // Recreated only if the workspace changes — recreating on every keystroke
    // would lose undo history and drop focus. Everything the frozen callbacks
    // above need from a later render comes through the refs, not this closure.
    [workspaceId],
  );

  // Closes the loop for `handlePaste`, which is captured when the editor is
  // created and therefore cannot see the editor it belongs to.
  editorRef.current = editor;

  const selectCandidate = useCallback(
    (candidate: MentionCandidate) => {
      const query = mentionQueryRef.current;
      editor
        .chain()
        .focus()
        .insertContentAt({ from: query?.from ?? 0, to: query?.to ?? 0 }, [
          { type: 'mention', attrs: { id: candidate.id, label: candidate.name } },
          { type: 'text', text: ' ' },
        ])
        .run();
      // No explicit setMentionQuery(null) needed: the insert above dispatches
      // a transaction, which synchronously fires onUpdate/onSelectionUpdate,
      // and the cursor now sits after a trailing space — findMentionQuery
      // already returns null for that state.
    },
    [editor],
  );
  selectCandidateRef.current = selectCandidate;

  useEffect(() => {
    onEmptyChange?.(isEmpty);
  }, [isEmpty, onEmptyChange]);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        editor.commands.focus('end');
      },
      clear: () => {
        editor.commands.clearContent(true);
        setIsEmpty(true);
        setMentionQuery(null);
      },
      // Stripped here, not at the call site: an image node carries a blob
      // URL while it uploads, the server refuses any attribute but the
      // attachment id, and a caller that forgot would lose the whole message
      // rather than the one image. See imageNode.ts.
      getContent: () => stripTransientAttrs(editor.getJSON()),
      /** Nothing typed and nothing pasted. `onEmptyChange` reports the same
       * thing as it changes; this answers it on demand, for a caller that only
       * needs it at submit time. */
      isEmpty: () => editor.isEmpty,
    }),
    [editor],
  );

  return (
    <div
      className={cx(
        'relative',
        toolbar && 'rounded-[10px] border border-line focus-within:border-line-strong',
      )}
    >
      {toolbar && <Toolbar editor={editor} onUpload={onUpload} />}
      {isEmpty && !toolbar && (
        <div
          aria-hidden
          className={cx(
            'pointer-events-none absolute inset-0 px-0.5 py-1 text-faint',
            size === 'ask' ? 'fs-16 leading-relaxed' : 'fs-13',
          )}
        >
          {placeholder}
        </div>
      )}
      {/* The min-height goes on the ProseMirror node itself rather than this
          wrapper: a wrapper taller than its editor is dead space where a click
          does not land in the text. */}
      <EditorContent
        editor={editor}
        className={cx(
          toolbar ? 'px-3.5 py-3 [&_.ProseMirror]:min-h-[6em]' : 'px-0.5 py-1',
          size === 'ask' && 'fs-16 leading-relaxed [&_.ProseMirror]:min-h-[4rem]',
        )}
      />
      {mentionQuery !== null && menuPosition !== null && candidates.length > 0 && (
        <MentionMenu
          candidates={candidates}
          highlightedIndex={highlightedIndexRef.current}
          left={menuPosition.left}
          top={menuPosition.top}
          onSelect={selectCandidate}
          onHover={setHighlightedIndex}
        />
      )}
    </div>
  );
});
